/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { Component, ChangeDetectionStrategy } from "@angular/core";
import { OnInit } from "@angular/core";
import { Input } from "@angular/core";
import { RuiStore } from "@anvilor/ngx-rui";
import { IRuiControlComponent } from "@anvilor/ngx-rui";
import { SurveyStatesControl } from "./survey-states-control";
import { DefaultMediaObserver } from "@anvilor/ngx-apps-core";
import { SubscriptionGroup } from "@anvilor/ngx-core-utils";
import { Observable } from "rxjs";
import { map } from "rxjs";
import { combineLatest } from "rxjs";
import { trigger } from "@angular/animations";
import { transition } from "@angular/animations";
import { style } from "@angular/animations";
import { animate } from "@angular/animations";
import { AnvilorFormsApp } from "../../../store/anvilor-forms-app-store.service";

@Component({
  selector: "app-survey-states-control",
  templateUrl: "./survey-states-control.component.html",
  styleUrls: ["./survey-states-control.component.scss"],
  animations: [
    trigger("mainFade", [
      transition(":leave", [
        style({ opacity: 1 }),
        animate("200ms ease", style({ opacity: 0, height: 0 })),
      ]),
    ]),
    trigger("successFade", [
      transition(":enter", [
        style({ opacity: 0 }),
        animate("500ms 330ms ease", style({ opacity: 1 })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SurveyStatesControlComponent implements IRuiControlComponent, OnInit {
  @Input() internalId!: string;
  control!: SurveyStatesControl;

  private subscriptions: SubscriptionGroup;

  currentDisplay$?: Observable<"main" | "post_submit" | "pre_open" | "post_close">;

  constructor(
    private rui: RuiStore,
    public media: DefaultMediaObserver,
    private app: AnvilorFormsApp
  ) {
    this.subscriptions = new SubscriptionGroup();
  }

  ngOnInit() {
    this.control = new SurveyStatesControl(this.rui, this.internalId);

    this.currentDisplay$ = combineLatest([
      this.app.submit.submissionAccepted$,
      this.app.openClose.isBeforeSurveyOpen$,
      this.app.openClose.isAfterSurveyClose$,
      this.control.postCloseControl$.pipe(map((c) => !!c)),
      this.control.preOpenControl$.pipe(map((c) => !!c)),
    ]).pipe(
      map(
        ([
          submissionAccepted,
          isBeforeOpen,
          isAfterClose,
          hasPostCloseControl,
          hasPreOpenControl,
        ]) =>
          submissionAccepted
            ? "post_submit"
            : isBeforeOpen && hasPreOpenControl
            ? "pre_open"
            : isAfterClose && hasPostCloseControl
            ? "post_close"
            : "main"
      )
    );
  }
}
